import { Beaker, Database, ShieldCheck, Truck, Sparkles } from "lucide-react";

/**
 * 3-column lab-process timeline with animated SVG connectors and glowing nodes.
 */
export default function ProcessTimeline() {
  const steps = [
    { icon: Beaker, t: "Synthesis", d: "Manufactured to ≥99% target purity under documented protocol with batch lineage from raw material to vial.", c: "var(--terra)" },
    { icon: Database, t: "Independent Test", d: "Each batch is sent to Koveralabs for HPLC purity and MS identity. We do not test our own product.", c: "var(--magenta)" },
    { icon: ShieldCheck, t: "COA Published", d: "Lab report is attached to the batch number and made public at the COA hub. Recalls auto-block release.", c: "var(--violet)" },
    { icon: Truck, t: "Sealed Mailer", d: "Lyophilized vial, COA card, batch sticker. 1–2 day USPS Priority. Tracked dispatch.", c: "var(--terra)" },
  ];

  return (
    <section className="relative overflow-hidden border-y border-[var(--line)] bg-[var(--paper-2)]/70">
      <div className="absolute inset-0 starfield opacity-30 pointer-events-none" />
      <div className="nebula-blob magenta absolute" style={{ width: 380, height: 380, top: "-20%", right: "10%", opacity: 0.18 }} />
      <div className="nebula-blob cyan absolute" style={{ width: 420, height: 420, bottom: "-25%", left: "10%", opacity: 0.18 }} />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-14">
          <div className="md:col-span-1">
            <div className="eyebrow"><Sparkles className="inline h-3 w-3 mr-1" />Lab pipeline</div>
            <h2 className="h-display text-4xl md:text-5xl mt-3">
              From <span className="gradient-text">stardust</span> to your bench.
            </h2>
            <p className="mt-4 text-[var(--ink-soft)] max-w-md">A four-stage protocol applied to every batch. Independent. Traceable. Public.</p>
          </div>
          <div className="md:col-span-2 hidden md:block">
            <svg viewBox="0 0 800 80" className="w-full h-20" aria-hidden>
              <defs>
                <linearGradient id="timeline-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#3FFFE0" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="#FF4FD8" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#8A6BFF" stopOpacity="0.9" />
                </linearGradient>
              </defs>
              <path d="M 20 40 Q 200 5, 400 40 T 780 40" stroke="url(#timeline-grad)" strokeWidth="1.6" strokeLinecap="round" fill="none" strokeDasharray="6 8" className="timeline-path" />
              {[20, 270, 530, 780].map((x, i) => (
                <g key={i}>
                  <circle cx={x} cy={40} r="7" fill="#07071A" stroke={["#3FFFE0", "#FF4FD8", "#8A6BFF", "#3FFFE0"][i]} strokeWidth="1.6" />
                  <circle cx={x} cy={40} r="2.5" fill={["#3FFFE0", "#FF4FD8", "#8A6BFF", "#3FFFE0"][i]} />
                </g>
              ))}
            </svg>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.t} className="timeline-card" style={{ "--node-color": s.c }}>
              <div className="timeline-node-num font-mono">{String(i + 1).padStart(2, "0")}</div>
              <div className="timeline-icon"><s.icon className="h-5 w-5" strokeWidth={1.6} /></div>
              <div className="timeline-title font-display">{s.t}</div>
              <p className="timeline-desc">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
